import React, { useEffect, useMemo } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import {
  Box,
  Divider,
  Typography,
  Button,
  Modal,
  IconButton,
} from "@mui/material";
import { useTheme } from "@mui/styles";
import CloseIcon from "@mui/icons-material/Close";
import SquarehubLoader from "bundles/common/components/SquarehubLoader";

const HiredPositionSelectionModal = ({
  open,
  onClose,
  jobApplications = [],
  selectedJobApplicationId,
  onSelectJobApplication,
  onConfirm,
  loading = false,
}) => {
  const theme = useTheme();
  const intl = useIntl();

  // Every still-active application is a candidate for the hire, spontaneous ones included.
  const displayApplications = useMemo(() => {
    const apps = Array.isArray(jobApplications) ? jobApplications : [];
    return apps.filter((app) => app?.status !== "closed");
  }, [jobApplications]);
  const hasOptions = displayApplications.length > 0;
  const isSelected = (appId) => String(selectedJobApplicationId) === String(appId);

  useEffect(() => {
    if (!open) return;
    if (!displayApplications.length) return;
    if (typeof onSelectJobApplication !== "function") return;

    const selectedValid = displayApplications.some(
      (app) => String(app?.id) === String(selectedJobApplicationId)
    );

    if (!selectedValid) {
      onSelectJobApplication(displayApplications[0].id);
    }
  }, [open, displayApplications, selectedJobApplicationId, onSelectJobApplication]);

  return (
    <Modal
      open={open}
      onClose={loading ? undefined : onClose}
      sx={{ display: "flex", alignItems: "center", justifyContent: "center", p: 2 }}
    >
      <Box
        sx={{
          width: "min(560px, calc(100vw - 24px))",
          backgroundColor: "#FFF",
          borderRadius: "20px",
          border: `1px solid ${theme.palette.background.neutral}`,
          boxShadow: "0px 20px 44px rgba(2, 51, 49, 0.14)",
          outline: "none",
          overflow: "hidden",
          position: "relative",
        }}
      >
        {loading ? (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              zIndex: 3,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "rgba(255, 255, 255, 0.75)",
            }}
          >
            <SquarehubLoader size={72} />
          </Box>
        ) : null}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            p: "20px 20px 14px",
            background: "linear-gradient(180deg, #F9FCFF 0%, #FFFFFF 100%)",
          }}
        >
          <Box>
            <Typography sx={{ fontWeight: 800, fontSize: "20px", color: theme.palette.text.main }}>
              <FormattedMessage id="recruiter.kanban.select_position_to_hire" />
            </Typography>
            <Typography sx={{ mt: 0.5, color: theme.palette.text.secondary, fontSize: "13px" }}>
              <FormattedMessage id="recruiter.kanban.select_position_description" />
            </Typography>
          </Box>
          <IconButton onClick={onClose} disabled={loading} size="small" sx={{ color: theme.palette.text.secondary }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <Divider />

        <Box sx={{ p: "18px 20px 20px" }}>
          {hasOptions ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {displayApplications.map((app) => (
                <Box
                  key={app.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => onSelectJobApplication?.(app.id)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      onSelectJobApplication?.(app.id);
                    }
                  }}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    p: "12px 14px",
                    borderRadius: "12px",
                    border: `1px solid ${isSelected(app.id) ? theme.palette.primary.main : theme.palette.secondary.grey}`,
                    backgroundColor: isSelected(app.id) ? "rgba(20, 83, 45, 0.08)" : "#FFF",
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                    "&:hover": {
                      backgroundColor: isSelected(app.id) ? "rgba(20, 83, 45, 0.12)" : theme.palette.secondary.box,
                    },
                  }}
                >
                  <Typography sx={{ fontWeight: isSelected(app.id) ? 700 : 500, color: theme.palette.text.main, fontSize: "14px" }}>
                    {app.job_offer?.job_title || intl.formatMessage({ id: "recruiter.kanban.spontaneous_application" })}
                  </Typography>
                  {isSelected(app.id) ? (
                    <Box
                      sx={{
                        minWidth: "22px",
                        height: "22px",
                        borderRadius: "999px",
                        backgroundColor: theme.palette.primary.main,
                        color: theme.palette.common.white,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontWeight: 800,
                        fontSize: "12px",
                      }}
                    >
                      ✓
                    </Box>
                  ) : null}
                </Box>
              ))}
            </Box>
          ) : (
            <Typography sx={{ color: theme.palette.text.secondary, fontSize: "14px" }}>
              <FormattedMessage id="no_data" defaultMessage="No data" />
            </Typography>
          )}

          <Box sx={{ mt: 2.5, display: "flex", justifyContent: "flex-end", gap: 1 }}>
            <Button
              onClick={onClose}
              disabled={loading}
              sx={{
                textTransform: "none",
                color: theme.palette.text.secondary,
                fontWeight: 600,
                borderRadius: "999px",
              }}
            >
              <FormattedMessage id="cancel" />
            </Button>
            <Button
              onClick={onConfirm}
              disabled={!selectedJobApplicationId || loading}
              sx={{
                textTransform: "none",
                fontWeight: 700,
                borderRadius: "999px",
                px: 3,
                color: theme.palette.common.white,
                backgroundColor: theme.palette.primary.main,
                "&:hover": {
                  backgroundColor: theme.palette.primary.dark,
                },
                "&.Mui-disabled": {
                  color: theme.palette.common.white,
                },
              }}
            >
              <FormattedMessage id="recruiter.kanban.hire_for_position" />
            </Button>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

export default HiredPositionSelectionModal;
